import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  SimpleGrid,
  Button,
  Input,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Switch,
  Tooltip,
  Text,
  IconButton,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { cqAPI } from "../../lib/Auth";
import EditableText from "../EditableText/EditableText";

function Bundles() {
  const [bundles, setBundles] = useState([]);
  const [products, setProducts] = useState([]);
  const [newBundle, setNewBundle] = useState({ name: "", description: "", items: [] });
  const { isOpen, onOpen, onClose } = useDisclosure({
    onClose: () => setNewBundle({ name: "", description: "", items: [] })
  });
  const toast = useToast();

  const fetchBundles = async () => {
    const response = await cqAPI.get("/settings/subscriptions/bundles");
    setBundles(response?.data?.bundles ?? []);
  };

  const fetchProducts = async () => {
    const response = await cqAPI.get("/settings/subscriptions");
    setProducts(response?.data?.availableProducts ?? []);
  };

  useEffect(() => {
    fetchBundles();
    fetchProducts();
  }, []);

  const handleSaveBundle = async () => {
    if (!newBundle.name || !newBundle.description) {
      toast({
        title: "Validation Error",
        description: "Name and description cannot be empty.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (newBundle.items.length === 0) {
      toast({
        title: "Validation Error",
        description: "At least one product must be added.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    for (const item of newBundle.items) {
      if (!item.product || !item.price) {
        toast({
          title: "Validation Error",
          description: "All products must have a selected price.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }

    try {
      const response = await cqAPI.post('/settings/subscriptions/bundles', newBundle);
      if (response.type === 'error') {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      setNewBundle({ name: "", description: "", items: [] }); // Reset the modal values
      await fetchBundles();
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while saving the bundle.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAddProduct = () => {
    setNewBundle((prevBundle) => ({
      ...prevBundle,
      items: [...prevBundle.items, { product: "", price: "" }],
    }));
  };

  const handleRemoveProduct = (index) => {
    setNewBundle((prevBundle) => ({
      ...prevBundle,
      items: prevBundle.items.filter((_, i) => i !== index),
    }));
  };

  const handleProductChange = (index, field, value) => {
    const updatedItems = newBundle.items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setNewBundle((prevBundle) => ({ ...prevBundle, items: updatedItems }));
  };

  const handleSave = async (bundleId, field, newValue) => {
    const response = await cqAPI.put('/settings/subscriptions/bundles', {
      _id: bundleId,
      field,
      value: newValue
    });
    if (response.type === 'error') {
      toast({
        title: "Error",
        description: response.msg,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
    setBundles(bundles.map(bundle => {
      if (bundle._id !== bundleId) { return bundle; }
      return {
        ...bundle,
        [field]: newValue
      }
    }));
    return true;
  };

  const calculateFixedPrice = (bundle) => {
    let total = 0;
    bundle.items.forEach(([product, price]) => {
      if (price.billing_scheme === 'per_unit') {
        total += price.unit_amount / 100;
      }
    });
    return total;
  };

  return (
    <Box
      p={4}
      overflowY="auto"
      maxHeight="100vh"
      width="100%"
      position="relative"
    >
      <Flex justifyContent="flex-end" alignItems="center" mb={4}>
        <Button leftIcon={<AddIcon />} onClick={onOpen}>
          Add Bundle
        </Button>
      </Flex>
      <SimpleGrid columns={{ base: 1, md: 3, lg: 3 }} spacing="20px">
        {bundles.map((bundle, index) => (
          <Box
            key={index}
            borderWidth="1px"
            borderRadius="lg"
            mb={4}
            width="100%"
          >
            <Box bg="blue.500" color="white" p={4} borderTopRadius="lg">
              <EditableText
                initialValue={bundle.name}
                onSave={(e) => handleSave(bundle._id, "name", e)}
                showPlaceholderInDisplayMode={false}
                placeholder="Name"
                pattern={/^.{1,15}$/}
                invalid="The name can be up to 15 characters and cannot be empty."
              />
            </Box>
            <Box p={4}>
              <Flex justifyContent="space-between" alignItems="left">
                <EditableText
                  initialValue={bundle.description}
                  onSave={(e) => handleSave(bundle._id, "description", e)}
                  showPlaceholderInDisplayMode={false}
                  placeholder="Description"
                  multiline={true}
                  pattern={/^.{1,55}$/}
                  invalid="The description can be up to 55 characters and cannot be empty."
                />
              </Flex>
              <Flex mt={4} alignItems="center" justifyContent="space-between">
                <Text>Private</Text>
                <Switch
                  isChecked={bundle.visible_to_admins_only}
                  onChange={(event) => { handleSave(bundle._id, "visible_to_admins_only", event.target.checked); }}
                />
              </Flex>
              <Flex mt={4} alignItems="center" justifyContent="space-between">
                <Text>Active</Text>
                <Switch
                  isChecked={bundle.active}
                  onChange={(event) => { handleSave(bundle._id, "active", event.target.checked); }}
                />
              </Flex>
              <Box mt={4}>
                <Text fontWeight="bold">Products and Prices:</Text>
                {bundle.items.map(([product, price], itemIndex) => (
                  <Box key={itemIndex} mt={2}>
                    <Text>
                      {product.name} - {price.billing_scheme === 'per_unit' ? `$${price.unit_amount / 100} per ${product.unit_label}` : 'Metered'}
                    </Text>
                    {price.billing_scheme === 'tiered' && price.tiers.map((tier, tierIndex) => (
                      <Box key={tierIndex} ml={4}>
                        <Text>
                          {`Tier ${tierIndex + 1}: $${tier.unit_amount / 100} per ${product.unit_label} `}
                          {tier.up_to !== null ? `between ${tierIndex === 0 ? 0 : price.tiers[tierIndex - 1]?.up_to + 1} and ${tier.up_to} ${product.unit_label}` : `Anything more or equal than ${tierIndex === 0 ? 0 : price.tiers[tierIndex - 1]?.up_to + 1}`}
                        </Text>
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
              <Box mt={4}>
                <Text fontWeight="bold">
                  Fixed Price: ${calculateFixedPrice(bundle)}
                </Text>
              </Box>
            </Box>
          </Box>
        ))}
      </SimpleGrid>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Bundle</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl id="bundle-name" mb={4}>
              <FormLabel>Name</FormLabel>
              <Input
                value={newBundle.name}
                onChange={(e) => setNewBundle({ ...newBundle, name: e.target.value })}
                placeholder="Bundle Name"
              />
            </FormControl>
            <FormControl id="bundle-description" mb={4}>
              <FormLabel>Description</FormLabel>
              <Input
                value={newBundle.description}
                onChange={(e) => setNewBundle({ ...newBundle, description: e.target.value })}
                placeholder="Bundle Description"
              />
            </FormControl>
            {newBundle.items.map((item, index) => {
              const product = products.find(p => p.id === item.product);
              return (
                <Flex key={index} mb={4} alignItems="center">
                  <FormControl flex="1" mr={2}>
                    <FormLabel>Product</FormLabel>
                    <Select
                      value={item.product}
                      onChange={(e) => handleProductChange(index, "product", e.target.value)}
                    >
                      <option value="">Select Product</option>
                      {products
                        .filter(p => !newBundle.items.some(i => i.product === p.id) || p.id === item.product)
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                    </Select>
                  </FormControl>
                  <FormControl flex="1" ml={2}>
                    <FormLabel>Pricing</FormLabel>
                    <Select
                      value={item.price}
                      onChange={(e) => handleProductChange(index, "price", e.target.value)}
                      isDisabled={!item.product}
                    >
                      <option value="">Select Pricing</option>
                      {product &&
                        product.plans.map((plan) => (
                          <option key={plan.id} value={plan.id}>{plan.nickname? `${plan.nickname}-` : ''}
                            {plan.billing_scheme === 'tiered' ? 
                              `$${plan.tiers[0].unit_amount / 100} per ${product.unit_label}` : 
                              `$${plan.amount / 100} per ${product.unit_label}`
                            }
                          </option>
                        ))}
                    </Select>
                  </FormControl>
                  <IconButton
                    icon={<DeleteIcon />}
                    onClick={() => handleRemoveProduct(index)}
                    ml={2}
                    alignSelf="flex-end"
                  />
                </Flex>
              );
            })}
            <Button onClick={handleAddProduct} leftIcon={<AddIcon />} colorScheme="teal" mb={4}>
              Add Product
            </Button>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} mr={3}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveBundle}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Bundles;
